/**
 * Route builders for the /settings area, exposed as one nested object so call
 * sites read like the url hierarchy: `routes.settings.stripe.portal(scope)`.
 * Every navigable result goes through buildRoute, which checks the bracket
 * pattern against Next's generated route table (typedRoutes) and types the
 * params from the generated ParamMap.
 */
import type { Route } from "next";
import { type WorkspaceScope, buildRoute } from "./shared";

export type CheckoutIntent = "payment" | "deploy";

/** Compute-plan tiers carried through the deploy-gate checkout round-trip. */
export type DeployCheckoutPlan = "starter" | "pro" | "business";
/** Where the deploy-gate dialog was opened from, for post-subscribe routing. */
export const DEPLOY_CHECKOUT_ORIGINS = ["create", "banner", "billing", "deploy"] as const;
export type DeployCheckoutOrigin = (typeof DEPLOY_CHECKOUT_ORIGINS)[number];

export const settingsRoutes = {
  general({ workspaceSlug }: WorkspaceScope): Route {
    return buildRoute("/[workspaceSlug]/settings/general", { workspaceSlug });
  },

  team({ workspaceSlug }: WorkspaceScope): Route {
    return buildRoute("/[workspaceSlug]/settings/team", { workspaceSlug });
  },

  apiKeys({ workspaceSlug }: WorkspaceScope): Route {
    return buildRoute("/[workspaceSlug]/settings/api-keys", { workspaceSlug });
  },

  billing({ workspaceSlug }: WorkspaceScope): Route {
    return buildRoute("/[workspaceSlug]/settings/billing", { workspaceSlug });
  },

  usage({ workspaceSlug }: WorkspaceScope): Route {
    return buildRoute("/[workspaceSlug]/settings/usage", { workspaceSlug });
  },

  limits({ workspaceSlug }: WorkspaceScope): Route {
    return buildRoute("/[workspaceSlug]/settings/limits", { workspaceSlug });
  },

  stripe: {
    portal({ workspaceSlug }: WorkspaceScope): Route {
      return buildRoute("/[workspaceSlug]/stripe/portal", { workspaceSlug });
    },

    checkout({
      workspaceSlug,
      intent,
      plan,
      from,
      returnTo,
    }: WorkspaceScope & {
      intent?: CheckoutIntent;
      plan?: DeployCheckoutPlan;
      from?: DeployCheckoutOrigin;
      returnTo?: string;
    }): Route {
      const query =
        intent || plan || from || returnTo
          ? {
              ...(intent ? { intent } : {}),
              ...(plan ? { plan } : {}),
              ...(from ? { from } : {}),
              ...(returnTo ? { returnTo } : {}),
            }
          : undefined;
      return buildRoute("/[workspaceSlug]/stripe/checkout", { workspaceSlug }, query);
    },
  },
};
